/* eslint-disable jsx-a11y/label-has-associated-control -- label+htmlFor+id é a
   associação usada em toda listagem do app (ver ClienteList); a regra do airbnb
   exige TAMBÉM o aninhamento, o que quebraria o grid do Field. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Loading from '../../components/Loading';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import StatusBadge from '../../components/StatusBadge';
import usePermissions from '../../components/Menu/MenuItems/hooks/usePermissions';
import { notifyApiError } from '../../utils/feedback';
import { listUsers } from '../../services/users/usersApi';
import { listRoles } from '../../services/users/rolesApi';

import { EmptyState } from '../../styles/feedback/EmptyState';
import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../styles/layout/ActionBar';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { ClickableRow, TextLink } from '../../styles/ui/Link';
import { CtaButton } from '../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Usuários — listagem (handoff 0110, refactor Gen3 do backend).
//
// Reescrita em 2026-09-14 no padrão obrigatório de listagem (`navegacao.md`
// cap. 1): URL = fonte da verdade, busca explícita, sem fetch em abertura fria,
// sentinela `applied=1`, paginação server-side. Antes disso a tela buscava no
// mount, ignorava a paginação e lia `u.Role.role_name` (o Gen3 devolve `role`,
// que pode ser `null` em conta legada).
//
// SEM ordenação de propósito: o contrato de `GET /users` não declara whitelist
// de `order_by`, e cabeçalho clicável que não ordena é a "ordenação fantasma"
// proibida pela regra 1.10.
// ---------------------------------------------------------------------------

const ITEMS_PER_PAGE = 20;

// Chaves = nomes dos query params da URL = nomes do backend (snake_case). A
// tradução para os args camelCase do service acontece em toServiceArgs().
//
// `status` nasce em 'active' (e não vazio) porque o backend o exige: ausente é
// 422. "Todos" é um valor de verdade (`all`), não a ausência de filtro.
const DEFAULT_FILTERS = {
   q: '',
   status: 'active',
   role_id: '',
};

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

// snake_case (URL/estado) → camelCase (args do service).
function toServiceArgs(filters, page) {
   return {
      page,
      perPage: ITEMS_PER_PAGE,
      q: filters.q,
      status: filters.status,
      roleId: filters.role_id,
   };
}

export default function ListUsers() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission('iam:user:create');
   // O filtro por papel depende do catálogo de OUTRO módulo (ver o efeito de
   // carga abaixo).
   const canFilterByRole = hasPermission('iam:role:read');

   const [searchParams, setSearchParams] = useSearchParams();
   // Leitura única, no mount: deep-link, F5 e volta-do-detalhe re-hidratam a
   // busca; URL limpa = abertura fria, que NÃO dispara requisição.
   const hasInitialQueryParams = searchParams.toString() !== '';

   // `filters` é o rascunho (ligado aos inputs); `appliedFilters` é o último
   // efetivamente pesquisado — é ele que a paginação usa, para não paginar com
   // filtro digitado mas não confirmado.
   const [filters, setFilters] = useState(() =>
      readFiltersFromUrl(searchParams)
   );
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [users, setUsers] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);
   const [hasSearched, setHasSearched] = useState(hasInitialQueryParams);

   // Catálogo de papéis do filtro. Não é a busca da listagem — é carga de
   // catálogo, e por isso roda no mount mesmo em abertura fria.
   const [roles, setRoles] = useState([]);

   const didInitialFetch = useRef(false);

   const commitToUrl = useCallback(
      (values, page) => {
         // `applied=1` marca "houve busca": sem ele, uma pesquisa que não
         // produz params (raro aqui, já que `status` sempre vai) seria
         // indistinguível da abertura fria ao voltar do detalhe.
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const fetchUsers = useCallback(async (values, page) => {
      setIsLoading(true);
      try {
         const result = await listUsers(toServiceArgs(values, page));
         setUsers(result.users);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         notifyApiError(err, { fallback: 'Erro ao carregar usuários.' });
         setUsers([]);
         setTotalPages(1);
         setTotalCount(0);
      } finally {
         setIsLoading(false);
         setHasSearched(true);
      }
   }, []);

   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchUsers(appliedFilters, currentPage);
   }, [fetchUsers, hasInitialQueryParams, appliedFilters, currentPage]);

   // Catálogo de papéis do filtro — dependência CROSS-MODULE desta tela.
   //
   // `GET /roles` exige `iam:role:read`, que é chave de OUTRO módulo: um perfil
   // com `iam:user:read` (acesso legítimo a esta tela) pode não tê-la. Por isso
   // duas camadas: não pedir o catálogo para quem não tem a chave, e marcar a
   // leitura como não-destrutiva para o caso de o grant divergir do que o store
   // acha que tem — sem isso o 403 viraria `window.location = '/403'` e a
   // listagem inteira ficaria inacessível por causa de um filtro acessório.
   // Sem catálogo, o filtro "Papel" não é renderizado (filtro que não filtra é
   // pior que filtro ausente).
   useEffect(() => {
      if (!canFilterByRole) return undefined;

      let mounted = true;

      listRoles({ skipForbiddenRedirect: true })
         .then((list) => {
            if (mounted) setRoles(list);
         })
         .catch((err) => {
            if (!mounted) return;
            setRoles([]);
            if (err?.kind === 'forbidden') return; // já tratado: filtro some
            notifyApiError(err, { fallback: 'Erro ao carregar os papéis.' });
         });

      return () => {
         mounted = false;
      };
   }, [canFilterByRole]);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const handleSearch = (event) => {
      event.preventDefault();
      setAppliedFilters(filters);
      commitToUrl(filters, 1);
      fetchUsers(filters, 1);
   };

   // Limpa rascunho, aplicados, resultados e URL — SEM disparar busca (1.7).
   const handleClear = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setUsers([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setHasSearched(false);
      setSearchParams({}, { replace: true });
   };

   const handlePageChange = (page) => {
      commitToUrl(appliedFilters, page);
      fetchUsers(appliedFilters, page);
   };

   const hasResults = users.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Usuários</PageTitle>
               <PageSubtitle>
                  Contas de acesso ao sistema — papel, situação e PDVs operáveis.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               {canCreate && (
                  <CtaButton
                     type="button"
                     disabled={isLoading}
                     onClick={() => navigate('/users/new')}
                  >
                     + Criar usuário
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Filtros ───────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="filter-q">Nome ou e-mail</label>
                        <input
                           id="filter-q"
                           type="text"
                           placeholder="Parte do nome ou do e-mail..."
                           autoComplete="off"
                           value={filters.q}
                           onChange={(e) => updateField('q', e.target.value)}
                        />
                     </Field>

                     <Field className={canFilterByRole ? 'col-3' : 'col-6'}>
                        <label htmlFor="filter-status">Situação</label>
                        <select
                           id="filter-status"
                           value={filters.status}
                           onChange={(e) =>
                              updateField('status', e.target.value)
                           }
                        >
                           <option value="active">Ativos</option>
                           <option value="inactive">Excluídos</option>
                           <option value="all">Todos</option>
                        </select>
                     </Field>

                     {canFilterByRole && (
                        <Field className="col-3">
                           <label htmlFor="filter-role">Papel</label>
                           <select
                              id="filter-role"
                              value={filters.role_id}
                              onChange={(e) =>
                                 updateField('role_id', e.target.value)
                              }
                           >
                              <option value="">Todos</option>
                              {roles.map((role) => (
                                 <option key={role.id} value={String(role.id)}>
                                    {role.name}
                                 </option>
                              ))}
                           </select>
                        </Field>
                     )}
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <TextLink
                           as="button"
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </TextLink>
                        <CtaButton type="submit" disabled={isLoading}>
                           Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>
                  Resultados{totalCount ? ` (${totalCount})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults && (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>Nome</th>
                                 <th>E-mail</th>
                                 <th>Papel</th>
                                 <th className="center">Situação</th>
                              </tr>
                           </thead>
                           <tbody>
                              {users.map((user) => (
                                 <ClickableRow
                                    key={user.id}
                                    onClick={() => navigate(`/users/${user.id}`)}
                                 >
                                    <td>{user.nome || '—'}</td>
                                    <td>{user.email || '—'}</td>
                                    {/* `role` é opcional no contrato: conta
                                        legada pode não ter papel. */}
                                    <td>{user.role?.name ?? '—'}</td>
                                    <td className="center">
                                       <StatusBadge
                                          variant={
                                             user.is_active ? 'green' : 'red'
                                          }
                                       >
                                          {user.is_active ? 'Ativo' : 'Excluído'}
                                       </StatusBadge>
                                    </td>
                                 </ClickableRow>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                        note={`Página ${currentPage} de ${totalPages} · ${ITEMS_PER_PAGE} por página`}
                     />
                  </>
               )}

               {/* Os dois vazios são diferentes: abertura fria convida a
                   pesquisar; busca sem resultado pede outro filtro. */}
               {!hasResults && !hasSearched && (
                  <EmptyState
                     title="Pesquise para começar"
                     description="Informe nome ou e-mail, escolha a situação e clique em Pesquisar."
                  />
               )}

               {!hasResults && hasSearched && (
                  <EmptyState
                     title="Nenhum usuário encontrado"
                     description="Ajuste os filtros e pesquise novamente."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
